// 偵測區域 `kinio_motion.zones`（SPEC #ls2505-334）：codec + 編輯器幾何。VMS 與之後的 web client 共用這一份
// （app/home-base web 目前另有一份 lib/kinio/zones.ts，內容相同；home-base web 改用 web-sdk 時刪掉那份）。
//
// wire：JSON 陣列 `[{x, y, w, h}]`，百分比整數 0–100，原點左上，`x + w ≤ 100`、`y + h ≤ 100`，最多 8 個；
// 空陣列 = 全畫面偵測。參考實作 component/protocol `shapes.go` Zones；golden 在 testdata/zones/
// （照抄 component/protocol testdata/zones；iOS / Android / home-base web 跑同一組）。只認這個形狀，不收舊格式。
//
// 幾何（三平台一致）：畫面座標 → 百分比浮點；放開時四個邊各自夾到 [0,100] 再四捨五入，
// `x = round(left)`、`w = round(right) - x`（y / h 同理）→ 自然保證 `x + w ≤ 100`。

export interface Zone {
  x: number;
  y: number;
  w: number;
  h: number;
}

export const ZONES_KEY = 'kinio_motion.zones';
export const ZONE_ENABLED_KEY = 'kinio_motion.zone_enabled';
/** 最多幾個矩形。 */
export const MAX_ZONES = 8;
/** 編輯器裡矩形的最小寬高（百分比）：新畫的小於這個丟掉，縮放停在這裡。 */
export const MIN_ZONE_SIZE = 5;

// ---- codec ----

function isObject(v: unknown): v is Record<string, unknown> {
  return typeof v === 'object' && v !== null && !Array.isArray(v);
}

/** 0–100 的整數值（10.0、1e1 算 10；布林 / 字串 / null / 物件都不算）；`-0` 正規化成 0。 */
function percent(v: unknown): number | null {
  if (typeof v !== 'number' || !Number.isFinite(v) || !Number.isInteger(v) || v < 0 || v > 100) return null;
  return v + 0;
}

function parseZone(v: unknown): Zone | null {
  if (!isObject(v)) return null;
  const x = percent(v.x);
  const y = percent(v.y);
  const w = percent(v.w);
  const h = percent(v.h);
  if (x === null || y === null || w === null || h === null) return null;
  if (w < 1 || h < 1 || x + w > 100 || y + h > 100) return null;
  return { x, y, w, h };
}

/**
 * 已經解開的 JSON 值 → 矩形清單。不是陣列 → null（整個值不收）；
 * 是陣列 → 先取前 8 個，再逐一丟掉不合法的（不硬修），留下的順序不變。
 */
export function parseZones(raw: unknown): Zone[] | null {
  return parseRects(raw, MAX_ZONES);
}

/** 偵測區域與隱私遮罩共用的矩形規則（只差上限）：不是陣列 → null；先取前 max 個，再逐一丟掉不合法的。 */
export function parseRects(raw: unknown, max: number): Zone[] | null {
  if (!Array.isArray(raw)) return null;
  const out: Zone[] = [];
  for (const v of raw.slice(0, max)) {
    const z = parseZone(v);
    if (z) out.push(z);
  }
  return out;
}

/** 寫出的 wire 值：只帶 x / y / w / h 四個 key、最多 MAX_ZONES 個（超過的不寫，跟設備 / parseZones 的「取前 8 個」一致）；null / undefined / 空 → `[]`（不是 null）。 */
export function encodeZones(zones: readonly Zone[] | null | undefined): Zone[] {
  return encodeRects(zones, MAX_ZONES);
}

export function encodeRects(zones: readonly Zone[] | null | undefined, max: number): Zone[] {
  return (zones ?? []).slice(0, max).map((z) => ({ x: z.x + 0, y: z.y + 0, w: z.w + 0, h: z.h + 0 }));
}

export function zonesEqual(a: readonly Zone[], b: readonly Zone[]): boolean {
  return a.length === b.length && a.every((z, i) => z.x === b[i].x && z.y === b[i].y && z.w === b[i].w && z.h === b[i].h);
}

/** 收斂後判斷設備是否原樣採用：reported 解析後的矩形清單 == 送出的清單（reported 解析失敗 = 不相等；`adopted()` 用 === 比不了陣列）。 */
export function zonesConverged(reported: unknown, sent: unknown): boolean {
  const r = parseZones(reported);
  const s = parseZones(sent);
  return r !== null && s !== null && zonesEqual(r, s);
}

// ---- 幾何 ----

function clamp(v: number, lo: number, hi: number): number {
  return Math.min(hi, Math.max(lo, v));
}

/** 正數 half-up（JS Math.round 對正數就是）；-0 → 0。 */
function round(v: number): number {
  return Math.round(v) + 0;
}

/** 畫面座標（相對繪圖區左上）→ 百分比浮點。繪圖區寬 / 高是 0 時回 0。 */
export function toPercent(px: number, size: number): number {
  return size > 0 ? (px / size) * 100 : 0;
}

/**
 * 任意兩點（拖曳方向可以反向）→ 整數矩形：先排好左右上下，四個邊各自夾到 [0,100] 再四捨五入。
 * 不檢查最小尺寸（預覽用；新畫的要不要留看 `drawnZone`）。
 */
export function rectFromPoints(x0: number, y0: number, x1: number, y1: number): Zone {
  const left = round(clamp(Math.min(x0, x1), 0, 100));
  const right = round(clamp(Math.max(x0, x1), 0, 100));
  const top = round(clamp(Math.min(y0, y1), 0, 100));
  const bottom = round(clamp(Math.max(y0, y1), 0, 100));
  return { x: left, y: top, w: right - left, h: bottom - top };
}

/** 新畫的矩形：`w < 5` 或 `h < 5` → null（丟掉）。 */
export function drawnZone(x0: number, y0: number, x1: number, y1: number): Zone | null {
  const z = rectFromPoints(x0, y0, x1, y1);
  return z.w < MIN_ZONE_SIZE || z.h < MIN_ZONE_SIZE ? null : z;
}

/** 移動（dx / dy 是百分比浮點的位移）：寬高不變，x 夾在 [0, 100-w]、y 夾在 [0, 100-h]，再四捨五入。 */
export function moveZone(z: Zone, dx: number, dy: number): Zone {
  return {
    x: round(clamp(z.x + dx, 0, 100 - z.w)),
    y: round(clamp(z.y + dy, 0, 100 - z.h)),
    w: z.w,
    h: z.h,
  };
}

export type ZoneCorner = 'nw' | 'ne' | 'sw' | 'se';

/**
 * 一個軸的縮放：拖的那個邊夾到 [0,100] 再四捨五入，對邊固定；長度 < 5 → 停在 5。
 * 對邊離畫面邊緣不到 5（設備回報的 1×1 貼邊矩形）→ 對邊往內推，維持 5 且不出界。
 */
function resizeAxis(start: number, len: number, dragHigh: boolean, p: number): [number, number] {
  const edge = round(clamp(p, 0, 100));
  if (dragHigh) {
    let s = start;
    const l = Math.max(MIN_ZONE_SIZE, edge - s);
    if (s + l > 100) s = 100 - l;
    return [s, l];
  }
  const end = start + len;
  let s = Math.min(edge, end - MIN_ZONE_SIZE);
  if (s < 0) s = 0;
  const l = Math.max(MIN_ZONE_SIZE, end - s);
  return [s, l];
}

/** 四角縮放：對角固定，拖的角到 (px, py)（百分比浮點）；最小 5×5（縮放不刪掉既有矩形）。 */
export function resizeZone(z: Zone, corner: ZoneCorner, px: number, py: number): Zone {
  const [x, w] = resizeAxis(z.x, z.w, corner === 'ne' || corner === 'se', px);
  const [y, h] = resizeAxis(z.y, z.h, corner === 'sw' || corner === 'se', py);
  return { x, y, w, h };
}
